import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/Profile.css";
import "../../styles/variables.css";
import { fetchUserData } from "../../utils/api";
import PageHeader from "../../components/PageHeader";
import WorkspacePage from "../../components/WorkspacePage";

const LIST_SECTIONS = [
  {
    key: "departments",
    title: "Department",
    icon: "icon-department",
    editPath: "/edit-department",
    emptyLabel: "No department assigned",
  },
  {
    key: "jobRoles",
    title: "Job Role",
    icon: "icon-job",
    editPath: "/edit-job-role",
    emptyLabel: "Not set",
  },
  {
    key: "purposeForAccess",
    title: "Purpose for Access",
    icon: "icon-purpose",
    editPath: "/edit-purpose",
    emptyLabel: "Not set",
  },
];

const getInitials = (userData) => {
  const first = userData?.firstName?.trim()?.[0] || "";
  const last = userData?.lastName?.trim()?.[0] || "";
  return `${first}${last}`.toUpperCase() || "?";
};

const getFullName = (userData) => {
  const name =
    `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();
  return name || "Not set";
};

const formatPhoneNumber = (value) => {
  if (!value) return "Not set";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value;
};

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const hasMountedRef = useRef(false);

  const loadUserData = useCallback(async (isInitialLoad) => {
    if (isInitialLoad) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);
    try {
      const data = await fetchUserData();
      setUserData(data);
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load profile data. Please try again.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadUserData(true);
  }, [loadUserData]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    loadUserData(false);
  }, [location.key, loadUserData]);

  useEffect(() => {
    if (location.state?.toastMessage) {
      setToastMessage(location.state.toastMessage);
      const timer = setTimeout(() => {
        setToastMessage(null);
        navigate(location.pathname, { replace: true, state: {} });
      }, 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state, location.pathname, navigate]);

  const handleEdit = (path) => navigate(path);
  const handleRetry = () => loadUserData(!userData);

  if (isLoading) {
    return (
      <WorkspacePage>
        <PageHeader title="Profile" />
        <div className="loading-state">
          <div className="spinner" aria-hidden="true" />
          <p>Loading profile data...</p>
        </div>
      </WorkspacePage>
    );
  }

  if (!userData) {
    return (
      <WorkspacePage>
        <PageHeader title="Profile" />
        <div className="error-state">
          <p>{error || "No profile data available."}</p>
          <button onClick={handleRetry} className="retry-button">
            Retry
          </button>
        </div>
      </WorkspacePage>
    );
  }

  return (
    <WorkspacePage>
      {toastMessage && <div className="profile-toast">{toastMessage}</div>}

      <PageHeader title="Profile" />

      {error && (
        <div className="profile-inline-error">
          <span>{error} Showing the last loaded profile.</span>
          <button onClick={handleRetry} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className={`profile-card${isRefreshing ? " is-refreshing" : ""}`}>
        <div className="profile-header">
          <div className="profile-avatar">{getInitials(userData)}</div>
          <div className="profile-info">
            <h2 className="header">Full Name</h2>
            <p className="body-text">{getFullName(userData)}</p>
            <h2 className="header">Email</h2>
            <p className="body-text">{userData.email || "Not set"}</p>
          </div>
        </div>

        <div className="profile-details">
          <div className="details-column left-column">
            {LIST_SECTIONS.map(({ key, title, icon, editPath, emptyLabel }) => {
              const items = Array.isArray(userData[key]) ? userData[key] : [];
              return (
                <div className="detail-section" key={key}>
                  <div className="section-header">
                    <h3 className="header">{title}</h3>
                    <button
                      className="edit-button"
                      aria-label={`Edit ${title}`}
                      onClick={() => handleEdit(editPath)}
                    >
                      Edit
                    </button>
                  </div>
                  <div className="detail-cards">
                    {items.length > 0 ? (
                      items.map((item, index) => (
                        <div className="detail-card" key={`${key}-${index}`}>
                          <span className={`icon ${icon}`}></span>
                          <span className="body-text">{item}</span>
                        </div>
                      ))
                    ) : (
                      <div className="detail-card is-empty">
                        <span className={`icon ${icon}`}></span>
                        <span className="body-text">{emptyLabel}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="details-column right-column">
            <div className="detail-section">
              <div className="section-header">
                <h3 className="header">Work Phone Number</h3>
                <button
                  className="edit-button"
                  aria-label="Edit Work Phone Number"
                  onClick={() => handleEdit("/edit-work-phone")}
                >
                  Edit
                </button>
              </div>
              <div className="detail-card">
                <span className="icon icon-phone"></span>
                <span className="body-text">
                  {formatPhoneNumber(userData.workPhoneNumber)}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <div className="section-header">
                <h3 className="header">Advisor</h3>
                <button
                  className="edit-button"
                  aria-label={
                    userData.advisor && userData.advisor !== "Not Set"
                      ? "Edit Advisor"
                      : "Add Advisor"
                  }
                  onClick={() => handleEdit("/edit-advisor")}
                >
                  {userData.advisor && userData.advisor !== "Not Set"
                    ? "Edit"
                    : "Add"}
                </button>
              </div>
              <div
                className={`detail-card${!userData.advisor || userData.advisor === "Not Set" ? " is-empty" : ""}`}
              >
                <span className="icon icon-advisor"></span>
                <span className="body-text">
                  {userData.advisor && userData.advisor !== "Not Set"
                    ? userData.advisor
                    : "Not set"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkspacePage>
  );
};

export default Profile;
